import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DailyIframe from "@daily-co/daily-js";
import {
  Video, Plus, X, Loader2, Copy, Trash2, ArrowLeft, CalendarClock, Clock3, Info, AlertTriangle,
  Maximize2, Minimize2, LogOut,
} from "lucide-react";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { useAuth } from "../context/AuthContext";
import { UserAvatar } from "../components/UserAvatar";
import { TenantLogo } from "../components/TenantLogo";
import ConfirmModal from "../components/ConfirmModal";

const WATTLINE_LOGO = "/brand/wattline-logo-light.svg";

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  if (sameDay(d, now)) return `oggi ${time}`;
  if (sameDay(d, tomorrow)) return `domani ${time}`;
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} ${time}`;
};

const isExpired = (m) => (m.expires_at || "") <= new Date().toISOString();

const copyGuestLink = async (meetingId) => {
  try {
    const { data } = await api.get(`/meetings/${meetingId}/guest-link`);
    await navigator.clipboard.writeText(data.link);
    toast.success("Link invito copiato: condividilo anche con esterni");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Impossibile generare il link invito");
  }
};

// ─── Banner limiti piano video ──────────────────────────────────────
const UsageBanner = ({ usage }) => {
  if (!usage || !usage.warning) return null;
  const exhausted = usage.exhausted;
  return (
    <div
      className="rounded-lg border px-4 py-3 mb-4 flex items-start gap-2.5 text-sm"
      style={{
        background: exhausted ? "var(--wl-danger-bg, #FDECEC)" : "#FFF7E6",
        borderColor: exhausted ? "var(--wl-danger, #D64545)" : "#E8B54A",
        color: "var(--wl-ink)",
      }}
      data-testid="meetings-usage-banner"
    >
      <AlertTriangle size={16} className="mt-0.5 shrink-0"
                     style={{ color: exhausted ? "var(--wl-danger, #D64545)" : "#B07B10" }} />
      <div>
        <span className="font-medium">
          {exhausted ? "Limite mensile del servizio video raggiunto." : "Il servizio video si avvicina al limite mensile."}
        </span>{" "}
        Utilizzo: {Math.round(usage.used_minutes).toLocaleString("it-IT")} / {usage.limit_minutes.toLocaleString("it-IT")} minuti-partecipante ({usage.pct}%).
        {exhausted
          ? " Le nuove riunioni potrebbero non avviarsi fino al prossimo mese."
          : " Oltre il limite le riunioni potrebbero non avviarsi."}
      </div>
    </div>
  );
};

// ─── Modale creazione ───────────────────────────────────────────────
const CreateMeetingModal = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 2) { toast.error("Dai un titolo alla riunione"); return; }
    setSaving(true);
    try {
      const body = { title: title.trim() };
      if (when) body.scheduled_at = new Date(when).toISOString();
      const { data } = await api.post("/meetings", body);
      toast.success(`Stanza «${data.title}» creata`);
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wl-modal-overlay" onClick={() => !saving && onClose()}>
      <form className="wl-modal wl-modal--md" onSubmit={submit} onClick={(e) => e.stopPropagation()}
            role="dialog" aria-modal="true">
        <div className="wl-modal__header">
          <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true"><Video size={18} /></div>
          <div className="wl-modal__title-block">
            <h3 className="wl-modal__title">Nuova riunione</h3>
            <p className="wl-modal__desc">Stanza privata: si entra solo con l'invito. Resta attiva fino a 4 ore dopo l'orario della riunione.</p>
          </div>
          <button type="button" onClick={() => !saving && onClose()} aria-label="Chiudi"
                  className="text-text-muted hover:text-text-primary"><X size={16} /></button>
        </div>
        <div className="wl-modal__body space-y-3">
          <div className="wl-field">
            <label className="wl-label">Titolo *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                   placeholder="es. Allineamento back office" className="wl-input" autoFocus
                   data-testid="meeting-title-input" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Data e ora (facoltative)</label>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                   className="wl-input" data-testid="meeting-when-input" />
            <span className="wl-helper">Se programmata, comparirà anche in dashboard.</span>
          </div>
        </div>
        <div className="wl-modal__actions">
          <button type="button" onClick={onClose} disabled={saving} className="wl-btn wl-btn--ghost">Annulla</button>
          <button type="submit" disabled={saving || title.trim().length < 2}
                  className="wl-btn wl-btn--primary" data-testid="meeting-create-submit">
            {saving && <Loader2 size={14} className="animate-spin" />} Crea stanza
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Stanza riunione: ingresso co-branded + embed Daily full-viewport ─
const stagePillStyle = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "7px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.25)",
  background: "rgba(20,20,24,0.82)", color: "#fff", fontSize: 12, fontWeight: 500,
  cursor: "pointer", backdropFilter: "blur(6px)",
};

const MeetingRoom = ({ meeting, user, onBack }) => {
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [isFs, setIsFs] = useState(false);
  const hostRef = useRef(null);
  const stageRef = useRef(null);
  const callRef = useRef(null);

  useEffect(() => () => {
    try { callRef.current?.destroy(); } catch (e) { /* noop */ }
    callRef.current = null;
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      stageRef.current?.requestFullscreen?.().catch(() => {
        toast.error("Schermo intero non disponibile in questo browser");
      });
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const exitCall = async () => {
    if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch (e) { /* noop */ } }
    try { await callRef.current?.leave(); } catch (e) { /* noop */ }
    try { callRef.current?.destroy(); } catch (e) { /* noop */ }
    callRef.current = null;
    setJoined(false);
    onBack();
  };

  const join = async () => {
    if (callRef.current) return; // guard StrictMode / doppio click
    setJoining(true);
    try {
      const { data } = await api.get(`/meetings/${meeting.id}/join-token`);
      callRef.current = DailyIframe.createFrame(hostRef.current, {
        showLeaveButton: true,
        iframeStyle: { width: "100%", height: "100%", border: "0" },
      });
      callRef.current.on("left-meeting", () => {
        try { callRef.current?.destroy(); } catch (e) { /* noop */ }
        callRef.current = null;
        if (document.fullscreenElement) { document.exitFullscreen?.().catch(() => {}); }
        setJoined(false);
      });
      if (process.env.NODE_ENV !== "production") window.__wlCall = callRef.current;
      setJoined(true);
      await callRef.current.join({ url: data.url, token: data.token, userName: data.user_name });
    } catch (e) {
      try { callRef.current?.destroy(); } catch (err) { /* noop */ }
      callRef.current = null;
      setJoined(false);
      toast.error(e?.response?.data?.detail || "Errore nell'avvio della riunione");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="wl-page">
      <div className="wl-page__header" style={{ marginBottom: "12px" }}>
        <button type="button" onClick={onBack} className="wl-btn wl-btn--ghost wl-btn--sm mb-2"
                style={{ border: "1px solid var(--wl-border)" }} data-testid="meeting-back-btn">
          <ArrowLeft size={13} /> Tutte le riunioni
        </button>
        <h1 className="wl-page__title">{meeting.title}</h1>
      </div>

      {!joined && (
        <div className="bg-surface border border-border rounded-xl p-10 flex flex-col items-center gap-5 text-center"
             data-testid="meeting-lobby">
          <div className="flex items-center gap-4">
            <img src={WATTLINE_LOGO} alt="Wattline" className="h-9 rounded" />
            <TenantLogo maxHeight={40} testId="meeting-lobby-tenant-logo" />
          </div>
          <div>
            <div className="text-lg font-semibold text-text-primary">{meeting.title}</div>
            {meeting.scheduled_at && (
              <div className="text-sm text-text-secondary mt-1 inline-flex items-center gap-1.5">
                <CalendarClock size={13} /> {fmtWhen(meeting.scheduled_at)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <button type="button" onClick={join} disabled={joining}
                    className="wl-btn wl-btn--primary" data-testid="meeting-join-btn">
              {joining ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
              Entra nella riunione
            </button>
            <button type="button" onClick={() => copyGuestLink(meeting.id)}
                    className="wl-btn wl-btn--ghost" style={{ border: "1px solid var(--wl-border)" }}
                    data-testid="meeting-copy-link-lobby">
              <Copy size={13} /> Copia link invito
            </button>
          </div>
          <div className="text-[11px] text-text-muted max-w-md">
            <span className="inline-flex items-center gap-1"><Info size={11} /> Servizio video fornito tramite Daily.co.</span>{" "}
            Stanza privata: si entra solo con l'invito. Nessuna registrazione audio/video.
          </div>
        </div>
      )}

      {/* Stage full-viewport: 100% larghezza × viewport meno la sola topbar.
          Copre sidebar e padding pagina; i controlli fluttuano sul video. */}
      <div
        ref={stageRef}
        data-testid="meeting-stage"
        style={joined ? {
          position: "fixed",
          top: "var(--wl-topbar-height, 56px)",
          left: 0, right: 0, bottom: 0,
          zIndex: 50,
          background: "#0E0E11",
        } : { height: 0, overflow: "hidden" }}
      >
        <div ref={hostRef} style={{ width: "100%", height: "100%" }} data-testid="meeting-daily-container" />
        {joined && (
          <div style={{ position: "absolute", top: 46, right: 12, zIndex: 2, display: "flex", gap: 8 }}>
            <button type="button" onClick={toggleFullscreen} style={stagePillStyle}
                    data-testid="meeting-fullscreen-btn">
              {isFs ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              {isFs ? "Riduci" : "Schermo intero"}
            </button>
            <button type="button" onClick={exitCall} style={stagePillStyle}
                    data-testid="meeting-exit-btn">
              <LogOut size={13} /> Esci
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Riga riunione ──────────────────────────────────────────────────
const MeetingRow = ({ m, canDelete, onEnter, onDelete }) => {
  const expired = isExpired(m);
  return (
    <li className="py-3 px-1 flex items-center gap-3 flex-wrap" data-testid={`meeting-row-${m.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-text-primary truncate">
          {m.title}
          {expired && (
            <span className="wl-badge wl-badge--neutral ml-2" style={{ fontSize: "10px" }}
                  data-testid={`meeting-expired-${m.id}`}>Scaduta</span>
          )}
        </div>
        <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
          {m.scheduled_at ? (
            <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> {fmtWhen(m.scheduled_at)}</span>
          ) : (
            <span className="inline-flex items-center gap-1"><Clock3 size={11} /> creata {fmtWhen(m.created_at)}</span>
          )}
          <span>·</span>
          <span className="inline-flex items-center gap-1.5">
            <UserAvatar userId={m.created_by} name={m.created_by_name} size={14} />
            {m.created_by_name}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {!expired && (
          <>
            <button type="button" onClick={() => onEnter(m)} className="wl-btn wl-btn--primary wl-btn--sm"
                    data-testid={`meeting-enter-${m.id}`}>
              <Video size={12} /> Entra
            </button>
            <button type="button" onClick={() => copyGuestLink(m.id)}
                    className="wl-btn wl-btn--ghost wl-btn--sm" style={{ border: "1px solid var(--wl-border)" }}
                    aria-label="Copia link invito" data-testid={`meeting-copy-${m.id}`}>
              <Copy size={12} />
            </button>
          </>
        )}
        {canDelete && (
          <button type="button" onClick={() => onDelete(m)} className="wl-btn wl-btn--ghost wl-btn--sm"
                  style={{ color: "var(--wl-danger-text)" }} aria-label="Elimina"
                  data-testid={`meeting-delete-${m.id}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </li>
  );
};

export default function Meetings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [active, setActive] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [loadFailed, setLoadFailed] = useState(false);
  const load = async () => {
    try {
      const { data: d } = await api.get("/meetings");
      setData(d);
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
      setData((prev) => prev || { meetings: [] });
    }
  };
  useEffect(() => {
    load();
    api.get("/meetings/usage").then(({ data: u }) => setUsage(u)).catch(() => {});
  }, []);

  // Deep-link ?join=<id> dalla dashboard
  useEffect(() => {
    const joinId = searchParams.get("join");
    if (!joinId || !data) return;
    const m = (data.meetings || []).find((x) => x.id === joinId);
    if (m) setActive(m);
    setSearchParams({}, { replace: true });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const runDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/meetings/${deleting.id}`);
      toast.success("Riunione eliminata");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeleteBusy(false);
    }
  };

  if (active && data) {
    return <MeetingRoom meeting={active} user={user}
                        onBack={() => { setActive(null); load(); }} />;
  }

  const meetings = data?.meetings || [];
  const upcoming = meetings.filter((m) => !isExpired(m))
    .sort((a, b) => (a.scheduled_at || a.created_at).localeCompare(b.scheduled_at || b.created_at));
  const past = meetings.filter((m) => isExpired(m));

  return (
    <div className="wl-page">
      <div className="wl-page__header flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="wl-page__eyebrow">
            <Video size={12} strokeWidth={2} style={{ marginRight: 4, verticalAlign: "-1px" }} />
            Riunioni
          </div>
          <h1 className="wl-page__title">Riunioni video</h1>
          <p className="wl-page__meta">
            Stanze private del tuo team con link invito condivisibili anche con esterni. Servizio video fornito tramite Daily.co.
          </p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} className="wl-btn wl-btn--primary"
                data-testid="new-meeting-btn">
          <Plus size={14} /> Nuova riunione
        </button>
      </div>

      <UsageBanner usage={usage} />

      {data === null ? (
        <div className="text-sm text-text-muted py-8 text-center">
          <Loader2 size={18} className="animate-spin mx-auto mb-1" /> Carico le riunioni…
        </div>
      ) : loadFailed ? (
        <div className="bg-surface border border-border rounded-xl">
          <LoadErrorBox what="le riunioni" onRetry={load} testId="meetings-load-error" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-10 text-center" data-testid="meetings-empty">
          <Video size={30} strokeWidth={1.5} className="mx-auto mb-3" style={{ color: "var(--wl-text-muted)" }} />
          <div className="text-sm font-medium text-text-primary">Nessuna riunione ancora.</div>
          <div className="text-xs mt-1 text-text-secondary">Crea la prima stanza con «Nuova riunione».</div>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <section className="bg-surface border border-border rounded-xl px-4 py-2" data-testid="meetings-upcoming">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-text-secondary py-2">Riunioni attive e programmate</h2>
              <ul className="divide-y divide-border">
                {upcoming.map((m) => (
                  <MeetingRow key={m.id} m={m}
                              canDelete={isAdmin || m.created_by === user?.id}
                              onEnter={setActive} onDelete={setDeleting} />
                ))}
              </ul>
            </section>
          )}
          {past.length > 0 && (
            <section className="bg-surface border border-border rounded-xl px-4 py-2" data-testid="meetings-history">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-text-secondary py-2">Storico</h2>
              <ul className="divide-y divide-border">
                {past.map((m) => (
                  <MeetingRow key={m.id} m={m}
                              canDelete={isAdmin || m.created_by === user?.id}
                              onEnter={setActive} onDelete={setDeleting} />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {showCreate && (
        <CreateMeetingModal onClose={() => setShowCreate(false)}
                            onCreated={(m) => { load(); setActive(m); }} />
      )}
      {deleting && (
        <ConfirmModal
          open
          onOpenChange={(o) => { if (!o && !deleteBusy) setDeleting(null); }}
          title={`Eliminare la riunione «${deleting.title}»?`}
          description="La stanza video viene chiusa e rimossa: i link invito smettono di funzionare."
          variant="danger"
          confirmLabel="Elimina"
          cancelLabel="Annulla"
          loading={deleteBusy}
          onConfirm={runDelete}
          testId="confirm-delete-meeting"
        />
      )}
    </div>
  );
}
